import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_6fa72c44-1c40-4a4f-af1b-3eb11820d1a2/artifacts/om2zhbmr_logoellawedding-white.png";
const LOGO_CHOCO = "https://customer-assets.emergentagent.com/job_6fa72c44-1c40-4a4f-af1b-3eb11820d1a2/artifacts/gxcr3jm2_Logo%20chocolat.png";

const navLinks = [
  { label: "À Propos", path: "/a-propos" },
  { label: "Prestations", path: "/prestations" },
  { label: "Galerie", path: "/galerie" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function NavLink({ link, location, isTransparent }) {
  return (
    <Link
      to={link.path}
      data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-').replace(/[àâ]/g, 'a')}`}
      style={{
        fontSize: "11px",
        letterSpacing: "3px",
        textTransform: "uppercase",
        fontFamily: "'Inter', sans-serif",
        fontWeight: "400",
        color: isTransparent ? "rgba(247,243,234,0.85)" : "#3D1602",
        textDecoration: "none",
        transition: "opacity 0.3s ease",
        opacity: location.pathname === link.path ? 1 : 0.7,
        borderBottom: location.pathname === link.path ? "1px solid currentColor" : "1px solid transparent",
        paddingBottom: "2px",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = "1"}
      onMouseLeave={e => e.currentTarget.style.opacity = location.pathname === link.path ? "1" : "0.7"}
    >
      {link.label}
    </Link>
  );
}

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isMobile = useIsMobile();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isTransparent = isHome && !scrolled;

  return (
    <>
      <nav
        data-testid="main-navigation"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
          transition: "background-color 0.5s ease, border-color 0.5s ease",
          backgroundColor: isTransparent ? "transparent" : "rgba(247,243,234,0.95)",
          backdropFilter: scrolled ? "blur(8px)" : "none",
          borderBottom: isTransparent ? "1px solid transparent" : "1px solid rgba(61,22,2,0.08)",
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 28px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "80px" }}>

            {/* Left: Logo + nav links together */}
            {!isMobile ? (
              <div style={{ display: "flex", alignItems: "center", gap: "36px" }}>
                {/* Logo — proportionnel au texte */}
                <Link to="/" data-testid="nav-logo">
                  <img
                    src={isTransparent ? LOGO_WHITE : LOGO_CHOCO}
                    alt="ELLAA WEDDING"
                    style={{
                      height: "150px",
                      width: "auto",
                      objectFit: "contain",
                      display: "block",
                      transition: "opacity 0.4s ease",
                      filter: isTransparent ? "brightness(0) saturate(100%) invert(97%) sepia(8%) saturate(400%) hue-rotate(330deg) brightness(99%) contrast(92%)" : "none",
                    }}
                  />
                </Link>

                {/* Nav links — immédiatement à côté du logo */}
                {navLinks.map((link) => (
                  <NavLink key={link.path} link={link} location={location} isTransparent={isTransparent} />
                ))}
              </div>
            ) : (
              /* Mobile: logo centré + burger à droite */
              /* Mobile: logo centré + burger à droite */
              <div style={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
                {/* Spacer gauche — même largeur que le burger pour centrer parfaitement le logo */}
                <div style={{ width: "42px", flexShrink: 0 }} />

                {/* Logo centré */}
                <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                  {!isTransparent && (
                    <Link to="/" data-testid="nav-logo-mobile">
                      <img
                        src={LOGO_CHOCO}
                        alt="ELLAA WEDDING"
                        style={{ height: "150px", width: "auto", objectFit: "contain", display: "block" }}
                      />
                    </Link>
                  )}
                </div>

                {/* Burger à droite */}
                <button
                  data-testid="mobile-menu-btn"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Ouvrir le menu"
                  style={{
                    background: "none", border: "none", cursor: "pointer", padding: "10px",
                    color: isTransparent ? "#F7F3EA" : "#3D1602",
                    display: "flex", alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Menu size={22} strokeWidth={1.5} />
                </button>
              </div>
            )}

            {/* Right: CTA — desktop only */}
            {!isMobile && (
              <a
                href="https://calendly.com/ellaa-wedding/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-cta"
                style={{
                  fontSize: "11px",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: "400",
                  padding: "10px 22px",
                  border: `1px solid ${isTransparent ? "rgba(247,243,234,0.5)" : "#3D1602"}`,
                  color: isTransparent ? "#F7F3EA" : "#3D1602",
                  textDecoration: "none",
                  transition: "all 0.35s ease",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = "#3D1602";
                  e.currentTarget.style.color = "#F7F3EA";
                  e.currentTarget.style.borderColor = "#3D1602";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = isTransparent ? "#F7F3EA" : "#3D1602";
                  e.currentTarget.style.borderColor = isTransparent ? "rgba(247,243,234,0.5)" : "#3D1602";
                }}
              >
                Prendre RDV
              </a>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile full-screen overlay menu */}
      <div
        data-testid="mobile-menu-overlay"
        aria-hidden={!mobileOpen}
        style={{
          position: "fixed", inset: 0, zIndex: 100,
          backgroundColor: "#F7F3EA",
          transform: mobileOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflowY: "auto",
        }}
      >
        {/* Close button */}
        <button
          data-testid="mobile-menu-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Fermer le menu"
          style={{
            position: "absolute", top: "20px", right: "20px",
            background: "none", border: "none", cursor: "pointer",
            color: "rgba(61,22,2,0.5)", padding: "10px",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <X size={26} strokeWidth={1.5} />
        </button>

        {/* Main content — prend tout l'espace disponible, centré verticalement */}
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 24px 16px",
          width: "100%",
          minHeight: 0,
        }}>
          {/* Logo → accueil */}
          <Link to="/" onClick={() => setMobileOpen(false)}>
            <img
              src={LOGO_CHOCO}
              alt="ELLAA WEDDING"
              style={{ height: "100px", marginBottom: "28px", opacity: 0.9, display: "block" }}
            />
          </Link>

          {/* Nav links */}
          <nav style={{ display: "flex", flexDirection: "column", gap: "20px", textAlign: "center", width: "100%" }}>
            {[...navLinks].map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${i}`}
                style={{
                  fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
                  fontSize: "clamp(19px, 5vw, 26px)",
                  fontWeight: "300",
                  color: location.pathname === link.path ? "#3D1602" : "rgba(61,22,2,0.6)",
                  textDecoration: "none",
                  letterSpacing: "1px",
                  transition: "color 0.25s ease",
                  display: "block",
                  padding: "3px 0",
                }}
              >
                {link.label}
              </Link>
            ))}

            {/* CTA inside mobile menu */}
            <a
              href="https://calendly.com/ellaa-wedding/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-cta"
              style={{
                marginTop: "12px",
                display: "inline-block",
                fontSize: "10px",
                letterSpacing: "4px",
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
                fontWeight: "400",
                padding: "13px 32px",
                border: "1px solid rgba(61,22,2,0.3)",
                color: "#3D1602",
                textDecoration: "none",
              }}
            >
              Planifier un appel
            </a>
          </nav>
        </div>

        {/* Tagline — toujours en bas, dans le flux normal, jamais en chevauchement */}
        <div style={{
          flexShrink: 0,
          padding: "16px 24px 32px",
          textAlign: "center",
        }}>
          <p style={{
            fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
            fontStyle: "italic",
            fontSize: "clamp(13px, 3.5vw, 16px)",
            color: "rgba(61,22,2,0.35)",
            margin: 0,
          }}>
            L'amour orchestré avec précision.
          </p>
        </div>
      </div>
    </>
  );
}
